// Boton de ayuda de la barra superior. Siempre visible dentro del backoffice.
//
// Abre un menu con las tres puertas de entrada a la ayuda, en orden de
// especificidad: el manual entero, el capitulo de la pantalla actual, el
// recorrido guiado, y el modo señalar para preguntar por un elemento concreto.
//
// Es un menu y no un boton directo porque las cuatro cosas responden preguntas
// distintas. "¿Como funciona esto?" no es "¿que hace este boton?".

import { useEffect, useRef, useState } from 'react';
import { BookOpen, HelpCircle, MousePointerClick, Route } from 'lucide-react';
import { useHelp } from '@/help/HelpProvider';

/** Id de ayuda de la pantalla actual, publicado por la pagina en el DOM. */
function currentPageHelpId(): string | null {
  return document.querySelector('[data-help-page]')?.getAttribute('data-help-page') ?? null;
}

export default function HelpButton(): JSX.Element {
  const { openHelp, startTour, togglePointing, pointing, tourHere } = useHelp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const itemCls =
    'flex w-full items-start gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div ref={ref} className="relative" data-help-ui="button">
      <button
        type="button"
        data-help="shell.help"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Ayuda"
        title="Ayuda"
        className={`
          inline-flex items-center justify-center
          min-h-[2.75rem] min-w-[2.75rem] rounded-lg transition
          focus-visible:outline-2 focus-visible:outline-brand-light
          ${
            pointing
              ? 'bg-brand text-white'
              : 'text-ink-muted hover:text-ink hover:bg-surface-muted dark:hover:text-ink-inverted dark:hover:bg-surface-dark-muted'
          }
        `}
      >
        <HelpCircle size={18} aria-hidden="true" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-72 bg-surface dark:bg-surface-dark border border-surface-border dark:border-surface-dark-border rounded-lg shadow-lg overflow-hidden z-30"
        >
          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={() => {
              setOpen(false);
              openHelp(currentPageHelpId() ?? undefined);
            }}
          >
            <BookOpen size={16} className="mt-0.5 shrink-0 text-brand" aria-hidden="true" />
            <span>
              <span className="block font-medium">Manual de esta pantalla</span>
              <span className="block text-xs text-ink-muted">
                Abre el capítulo que explica dónde estás parado.
              </span>
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            className={itemCls}
            disabled={!tourHere}
            onClick={() => {
              setOpen(false);
              startTour();
            }}
          >
            <Route size={16} className="mt-0.5 shrink-0 text-brand" aria-hidden="true" />
            <span>
              <span className="block font-medium">Recorrido guiado</span>
              <span className="block text-xs text-ink-muted">
                {tourHere
                  ? `${tourHere.steps.length} pasos señalando cada parte.`
                  : 'Esta pantalla todavía no tiene recorrido.'}
              </span>
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={() => {
              setOpen(false);
              togglePointing();
            }}
          >
            <MousePointerClick
              size={16}
              className="mt-0.5 shrink-0 text-brand"
              aria-hidden="true"
            />
            <span>
              <span className="block font-medium">Señalar un elemento</span>
              <span className="block text-xs text-ink-muted">
                El cursor pasa a “?”. El próximo clic abre la explicación de lo que toques.
              </span>
            </span>
          </button>

          <div className="border-t border-surface-border dark:border-surface-dark-border" />

          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={() => {
              setOpen(false);
              openHelp();
            }}
          >
            <BookOpen size={16} className="mt-0.5 shrink-0 text-ink-subtle" aria-hidden="true" />
            <span>
              <span className="block font-medium">Índice del manual</span>
              <span className="block text-xs text-ink-muted">
                Todo el contenido, con buscador.
              </span>
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
